import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import {
    EDIT,
    IDLE, PENDING,
    ASC,
    AVERAGE, BAYSIAN,
    FIT, SCIENTIFIC,
} from "const"
import { StateType, hasAccess } from "client/utils"
import {
    PRM_QID_PRODUCT,
    StoreType,
    LoadQidDatapoints,
    PrepareResultDatapoints,
    SortQidModels,

    setQidResultDatapointList,

    setQidResultCalculationMethod,
    setQidResultFormat,
    setQidResultDecimal,

    setQidResultModelIds,
    setQidResultModel,
    setQidResultSafetyFunction,
    setQidResultOption,
} from "client/store"
import {
    Container,
    PageContent,
    PageForm,
    Loader,
    FormGroup,
    Label,
    SelectDropdown,
    SelectDropdownItem,
    Empty,
    IncreaseInput,

    TableWrapper,
    TableScroller,
    TableContentWrapper,
    TableRow,
    TableHeaderCell,
    TableCell,
    TableCellContentWrapper,
} from "client/components"

import Columns from "./columns"
import Rows from "./rows"

const Results = () => {
    const product = useSelector((state: StoreType) => state.qidProduct.productView)
    const modelList = useSelector((state: StoreType) => state.qidModel.modelList)
    const reloadDatapointList = useSelector((state: StoreType) => state.qidDatapoint.reloadList)

    const calculationMethod = useSelector((state: StoreType) => state.qidResult.calculationMethod)
    const format = useSelector((state: StoreType) => state.qidResult.format)
    const decimal = useSelector((state: StoreType) => state.qidResult.decimal)

    const modelIds = useSelector((state: StoreType) => state.qidResult.modelIds)
    const model = useSelector((state: StoreType) => state.qidResult.model)
    const safetyFunction = useSelector((state: StoreType) => state.qidResult.safetyFunction)
    const option = useSelector((state: StoreType) => state.qidResult.option)

    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [loadState, setLoadState] = useState<StateType>(IDLE)

    useEffect(() => {
        const sortedIds = SortQidModels(Object.values(modelList), "name", ASC, {})
        dispatch(setQidResultModelIds(sortedIds))
        dispatch(setQidResultModel(null))
    }, [modelList])

    useEffect(() => {
        dispatch(setQidResultSafetyFunction(null))
        dispatch(setQidResultOption(null))
    }, [model])

    useEffect(() => {
        dispatch(setQidResultOption(null))
    }, [safetyFunction])

    useEffect(() => {
        if (!reloadDatapointList) return
        if (!product || !product.id) return

        setLoadState(PENDING)
        LoadQidDatapoints({
            productId: product.id,
        }).then((datapoints) => {
            const datapointList = PrepareResultDatapoints(datapoints)
            dispatch(setQidResultDatapointList(datapointList))
            setLoadState(IDLE)
        })
    }, [product, reloadDatapointList])

    const columns = Columns()
    const rows = Rows(columns)

    const editAccess = hasAccess(PRM_QID_PRODUCT, EDIT)

    return (
        <Container>
            <PageContent>
                {
                    loadState === PENDING
                    && <Loader />
                }

                <PageForm>
                    <Row>
                        {
                            editAccess && (
                                <Col xs={4}>
                                    <FormGroup>
                                        <Label>{lang.calculationMethod}</Label>
                                        <SelectDropdown title={lang[calculationMethod]}>
                                            <SelectDropdownItem onClick={() => dispatch(setQidResultCalculationMethod(AVERAGE))}>
                                                {lang.average}
                                            </SelectDropdownItem>
                                            <SelectDropdownItem onClick={() => dispatch(setQidResultCalculationMethod(BAYSIAN))}>
                                                {lang.baysian}
                                            </SelectDropdownItem>
                                        </SelectDropdown>
                                    </FormGroup>
                                </Col>
                            )
                        }
                        <Col xs={4}>
                            <FormGroup>
                                <Label>{lang.format}</Label>
                                <SelectDropdown title={lang[format]}>
                                    <SelectDropdownItem onClick={() => dispatch(setQidResultFormat(FIT))}>
                                        {lang.fit}
                                    </SelectDropdownItem>
                                    <SelectDropdownItem onClick={() => dispatch(setQidResultFormat(SCIENTIFIC))}>
                                        {lang.scientific}
                                    </SelectDropdownItem>
                                </SelectDropdown>
                            </FormGroup>
                        </Col>
                        <Col xs={4}>
                            <FormGroup>
                                <Label>{lang.decimal}</Label>
                                <IncreaseInput
                                    onChange={(val) => dispatch(setQidResultDecimal(val))}
                                    value={decimal}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={4}>
                            <FormGroup>
                                <Label>{lang.model}</Label>
                                <SelectDropdown title={model ? model.name : lang.selectModel}>
                                    <SelectDropdownItem onClick={() => dispatch(setQidResultModel(null))}>
                                        <Empty />
                                    </SelectDropdownItem>
                                    {
                                        modelIds.map((id) => (
                                            <SelectDropdownItem key={id} onClick={() => dispatch(setQidResultModel(modelList[id]))}>
                                                {modelList[id] && modelList[id].name}
                                            </SelectDropdownItem>
                                        ))
                                    }
                                </SelectDropdown>
                            </FormGroup>
                        </Col>

                        <Col xs={4}>
                            <FormGroup>
                                <Label>{lang.safetyFunction}</Label>
                                <SelectDropdown title={safetyFunction ? safetyFunction.name : lang.selectSafetyFunction}>
                                    <SelectDropdownItem onClick={() => dispatch(setQidResultSafetyFunction(null))}>
                                        <Empty />
                                    </SelectDropdownItem>
                                    {
                                        model && model.safetyFunctions.map((row) => (
                                            <SelectDropdownItem key={row.id} onClick={() => dispatch(setQidResultSafetyFunction(row))}>
                                                {row.name}
                                            </SelectDropdownItem>
                                        ))
                                    }
                                </SelectDropdown>
                            </FormGroup>
                        </Col>

                        <Col xs={4}>
                            <FormGroup>
                                <Label>{lang.option}</Label>
                                <SelectDropdown title={option ? option.name : lang.selectOption}>
                                    <SelectDropdownItem onClick={() => dispatch(setQidResultOption(null))}>
                                        <Empty />
                                    </SelectDropdownItem>
                                    {
                                        safetyFunction && safetyFunction.options.map((row) => (
                                            <SelectDropdownItem key={row.id} onClick={() => dispatch(setQidResultOption(row))}>
                                                {row.name}
                                            </SelectDropdownItem>
                                        ))
                                    }
                                </SelectDropdown>
                            </FormGroup>
                        </Col>
                    </Row>
                </PageForm>

                <TableWrapper>
                    <TableScroller>
                        <TableContentWrapper>
                            <thead>
                                <TableRow size="sm" noHover>
                                    {
                                        columns.map((column) => (
                                            <TableHeaderCell
                                                key={column.field}
                                                cellWidth={column.cellWidth}
                                                cellMinWidth={column.cellMinWidth}
                                                cellMaxWidth={column.cellMaxWidth}
                                            >
                                                {
                                                    column.header
                                                    && <span>{column.header}</span>
                                                }
                                            </TableHeaderCell>
                                        ))
                                    }
                                </TableRow>
                            </thead>
                            <tbody>
                                { rows }
                                {
                                    rows.length === 0 && (
                                        <TableRow size="sm">
                                            <TableCell colSpan={100}>
                                                <TableCellContentWrapper>
                                                    <Empty />
                                                </TableCellContentWrapper>
                                            </TableCell>
                                        </TableRow>
                                    )
                                }
                            </tbody>
                        </TableContentWrapper>
                    </TableScroller>
                </TableWrapper>

            </PageContent>
        </Container>
    )
}
export default Results
